import { CheckCircleIcon, EyeIcon } from '@heroicons/react/20/solid';
import { formatDate, formatDateTime } from '../../common/formatDateText';
import { capitalizarPalabras } from '../../common/upperCaseWord';
import { Tooltip } from '../../components/smartTable/Tooltip';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const normalizeValue = (value) => {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	return '';
};

const MoneyCell = ({ value }) => {
	const n = Number(value);
	const showDash = value == null || value === '' || Number.isNaN(n) || n <= 0;
	return (
		<div className='flex w-full justify-center'>
			<p
				className={
					showDash
						? 'text-center text-sm text-slate-500'
						: 'rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'
				}
			>
				{showDash ? '-' : `$ ${value}`}
			</p>
		</div>
	);
};

const StatusBadge = ({ value }) => {
	const safe = normalizeValue(value).toLowerCase();
	if (!safe) return null;
	return (
		<div className='flex w-full justify-center'>
			<div
				className={classNames(
					safe === 'completado'
						? 'bg-emerald-100 text-emerald-600'
						: safe === 'prospecto'
							? 'bg-orange-100 text-orange-600'
							: safe === 'validando' || safe === 'validado'
								? 'bg-rose-100 text-rose-600'
								: 'bg-gray-100 text-gray-600',
					'flex w-[130px] justify-center rounded-md py-1 text-[.8rem] font-bold uppercase',
				)}
			>
				<span>{safe}</span>
			</div>
		</div>
	);
};

/**
 * @param {{
 *   typeUser: string,
 *   typeUserCanValidate: string[],
 *   typeUserCanView: string[],
 *   onOpenValidation: (preRegisterId: string) => void,
 * }} options
 */
export const buildCandidatesColumns = ({
	typeUser,
	typeUserCanValidate,
	typeUserCanView,
	onOpenValidation,
}) => [
	{
		accessorKey: 'status',
		id: 'status',
		header: 'Estatus',
		size: 180,
		enableSorting: true,
		cell: ({ getValue }) => <StatusBadge value={getValue()} />,
		meta: { label: 'Estatus', excelHeaders: ['Estatus'] },
	},
	{
		accessorKey: 'nameUser',
		id: 'nameUser',
		header: 'Candidato',
		size: 360,
		cell: ({ getValue, row }) => {
			const name = capitalizarPalabras(normalizeValue(getValue()));
			const email = normalizeValue(row.original?.email);
			return (
				<div className='flex w-full min-w-0 items-start gap-2'>
					<div className='min-w-0 flex-1 space-y-0.5'>
						<p className='min-w-0 truncate text-sm font-semibold text-slate-800'>{name || '-'}</p>
						{email ? (
							<p className='truncate text-xs text-slate-500' title={email}>
								{email}
							</p>
						) : (
							<p className='truncate text-xs text-slate-400'>-</p>
						)}
					</div>
				</div>
			);
		},
		meta: {
			label: 'Candidato',
			searchable: true,
			searchPlaceholder: 'Buscar nombre o correo...',
			excelHeaders: ['Nombre de solicitante', 'Correo electronico'],
		},
	},
	{
		accessorKey: 'createdAt',
		id: 'createdAt',
		header: 'Fecha de solicitud',
		size: 200,
		enableSorting: true,
		cell: ({ getValue }) => (
			<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
				{formatDateTime(getValue())}
			</p>
		),
		meta: { label: 'Fecha de solicitud', excelHeaders: ['Fecha de solicitud'] },
	},
	{
		accessorKey: 'subscriptionPayment',
		id: 'subscriptionPayment',
		header: 'Monto de la suscripción',
		size: 180,
		enableSorting: true,
		cell: ({ getValue, row }) => (
			<MoneyCell value={getValue() ?? row.original?.amount} />
		),
		meta: { label: 'Monto de la suscripción', excelHeaders: ['Monto de la suscripcion'] },
	},
	{
		accessorKey: 'amount',
		id: 'amount',
		header: 'Monto del pago',
		size: 160,
		enableSorting: true,
		cell: ({ getValue }) => <MoneyCell value={getValue()} />,
		meta: { label: 'Monto del pago', excelHeaders: ['Monto del pago'] },
	},
	{
		id: 'account',
		header: 'Cuenta',
		accessorFn: (row) =>
			[row?.accountName, row?.accountNumber].filter(Boolean).join(' · ') || '-',
		size: 240,
		enableSorting: false,
		cell: ({ row }) => {
			const name = row.original?.accountName || '-';
			const number = row.original?.accountNumber;
			const icon = row.original?.accountIcon;
			return (
				<div className='flex min-w-0 items-start gap-2'>
					{icon ? (
						<img className='mt-0.5 h-7 w-7 shrink-0 rounded object-contain' src={icon} alt='' />
					) : null}
					<div className='min-w-0 flex-1'>
						<p className='truncate text-sm font-semibold uppercase text-slate-700'>{name}</p>
						<p className='truncate text-xs text-slate-500'>{number || '-'}</p>
					</div>
				</div>
			);
		},
		meta: {
			label: 'Cuenta',
			excelHeaders: ['Nombre cuenta', 'Numero cuenta'],
		},
	},
	{
		accessorKey: 'dateBirth',
		id: 'dateBirth',
		header: 'Fecha de nacimiento',
		size: 220,
		enableSorting: true,
		cell: ({ getValue }) => (
			<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
				{formatDate(getValue())}
			</p>
		),
		meta: { label: 'Fecha de nacimiento', excelHeaders: ['Fecha de nacimiento'] },
	},
	{
		accessorKey: 'phone',
		id: 'phone',
		header: 'Teléfono',
		size: 180,
		enableSorting: false,
		cell: ({ getValue }) => <p className='capitalize'>{normalizeValue(getValue()) || '-'}</p>,
		meta: {
			label: 'Teléfono',
			searchable: true,
			searchPlaceholder: 'Buscar teléfono...',
			excelHeaders: ['Telefono'],
		},
	},
	{
		accessorKey: 'location',
		id: 'location',
		header: 'Ubicación',
		size: 220,
		enableSorting: true,
		cell: ({ getValue }) => (
			<p className='truncate text-sm text-slate-700'>{normalizeValue(getValue()) || '-'}</p>
		),
		meta: {
			label: 'Ubicación',
			searchable: true,
			searchPlaceholder: 'Buscar ubicación...',
			excelHeaders: ['Ubicacion'],
		},
	},
	{
		accessorKey: 'education',
		id: 'education',
		header: 'Nivel educativo',
		size: 220,
		enableSorting: true,
		cell: ({ getValue }) => (
			<p className='truncate text-sm text-slate-700'>{normalizeValue(getValue()) || '-'}</p>
		),
		meta: {
			label: 'Nivel educativo',
			searchable: true,
			searchPlaceholder: 'Buscar educación...',
			excelHeaders: ['Educacion'],
		},
	},
	{
		accessorKey: 'coordinador',
		id: 'coordinador',
		header: 'Coordinador',
		size: 260,
		enableSorting: false,
		cell: ({ getValue }) => (
			<p className='truncate text-sm font-semibold capitalize text-slate-700'>
				{capitalizarPalabras(normalizeValue(getValue())) || '-'}
			</p>
		),
		meta: { label: 'Coordinador', excelHeaders: ['Coordinador'] },
	},
	{
		id: 'action',
		header: '',
		size: 170,
		enableSorting: false,
		cell: ({ row }) => {
			const status = row.original?.status;
			const id = row.original?.id;
			const canValidate = typeUserCanValidate.includes(typeUser);
			const canView = typeUserCanView.includes(typeUser);

			if ((status === 'validando' || status === 'validado') && canValidate) {
				return (
					<div className='flex w-full justify-center'>
						<Tooltip title='Validar pago del candidato' placement='left'>
							<button
								type='button'
								onClick={() => onOpenValidation(id)}
								className='inline-flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 transition-colors hover:bg-gray-200'
							>
								<CheckCircleIcon className='block h-5 w-5' aria-hidden='true' />
								<span className='ml-2 font-semibold text-md'>Validar</span>						
							</button>
						</Tooltip>
					</div>
				);
			}

			if (status === 'completado' || status === 'prospecto') {
				return (
					<div className='flex w-full justify-center'>
						<Tooltip title='Ver detalle del proceso' placement='left'>
							<button
								type='button'
								onClick={() => onOpenValidation(id)}
								className='inline-flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 transition-colors hover:bg-gray-200'
							>
								<EyeIcon className='block h-5 w-5' aria-hidden='true' />
								<span className='ml-2 font-semibold text-md'>Detalle</span>
							</button>
						</Tooltip>
					</div>
				);
			}

			if (canView) {
				return (
					<div className='flex w-full justify-center'>
						<Tooltip title='Ver detalle del proceso' placement='left'>
							<button
								type='button'
								onClick={() => onOpenValidation(id)}
								className='inline-flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 transition-colors hover:bg-gray-200'
							>
								<EyeIcon className='block h-5 w-5' aria-hidden='true' />
								<span className='ml-2 font-semibold text-md'>Detalle</span>
							</button>
						</Tooltip>
					</div>
				);
			}

			return null;
		},
		meta: { label: 'Acciones', enableExcel: false, sticky: 'right' },
	},
];
